import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Check, ChevronLeft, Mail, ShieldOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { DownloadIcon, EyeIcon, RefreshCWIcon } from "../../components/icons";
import DataTable from "../../common/DataTable/DataTable";
import "../../common/DataTable/DataTable.css";
import PageErrorState from "../../common/PageErrorState/PageErrorState";
import Toast from "../../common/Toast/Toast";
import { emailRegex, emailccRegex } from "../../configs/App.Regex";
import EntityConstants from "../../configs/EntityConstants";
import { GET, POST } from "../../service/CRUDService";
import {
  classifyApiError,
  getApiErrorCopy,
} from "../../utils/classifyApiError";
import {
  getCredentialStatusTone,
  STATUS_TONE_STYLES,
} from "../../utils/credentialStatusUtils";
import { formatDateOnly } from "../../utils/formatDate";
import crossIcon from "../../resources/icons/cross-icon.svg";
import dangerIcon from "../../resources/icons/danger-icon.svg";
import correctGreenIcon from "../../resources/icons/correct-green.svg";
import idCardIcon from "../../resources/icons/id-card.svg";
// TODO: Re-enable when verification feature is implemented
// import arrowSquareUpRightIcon from "../../resources/icons/arrow-square-up-right.svg";
import CertificatePreviewModal from "../NotifyRecipients/CertificatePreviewModal";
import ConfirmSendModal from "../NotifyRecipients/ConfirmSendModal";
import EmailPreviewModal from "../NotifyRecipients/EmailPreviewModal";
import {
  getAvatarColors,
  getHolderInitials,
  getIssuanceStatusBadgeClass,
  getIssuanceStatusLabel,
  isNotifyEligibleStatus,
  isRevokeEligibleStatus,
  parseHolderDetailsPayload,
} from "./utils/holderUtils";

function HolderDetailsShimmer({ className = "" }) {
  return (
    <div className={`cf-shimmer rounded-lg ${className}`} aria-hidden="true" />
  );
}

const PAGE_LIMIT = 8;

const HolderDetailsPage = () => {
  const { t } = useTranslation(["holders", "workspace", "finance", "history"]);
  const { holderId: holderPublicId } = useParams();
  const navigate = useNavigate();
  const hasLoadedRef = useRef(false);
  const requestIdRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [holder, setHolder] = useState(null);
  const [summary, setSummary] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [offset, setOffset] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [lastIssuedAt, setLastIssuedAt] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [previewCredential, setPreviewCredential] = useState(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const [notifyRecipients, setNotifyRecipients] = useState([]);
  const [notifyExclusion, setNotifyExclusion] = useState(null);
  const [isMultipleSelect, setIsMultipleSelect] = useState(false);
  const [toEmails, setToEmails] = useState([]);
  const [currentToEmail, setCurrentToEmail] = useState("");
  const [toEmailError, setToEmailError] = useState("");
  const [ccEmails, setCcEmails] = useState([]);
  const [currentCcEmail, setCurrentCcEmail] = useState("");
  const [ccEmailError, setCcEmailError] = useState("");
  const [showConfirmSend, setShowConfirmSend] = useState(false);
  const [confirmSendData, setConfirmSendData] = useState(null);
  const [isSendingFromConfirm, setIsSendingFromConfirm] = useState(false);

  const loadDetails = useCallback(
    async (nextOffset = 0) => {
      if (!holderPublicId) return;
      const requestId = ++requestIdRef.current;
      const isInitialLoad = !hasLoadedRef.current;
      setError(null);
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setCredentialsLoading(true);
      }
      try {
        const response = await GET(
          `${EntityConstants.GET_HOLDER_DETAILS}${holderPublicId}?limit=${PAGE_LIMIT}&offset=${nextOffset}`,
        );
        const payload = await response.json();
        if (requestId !== requestIdRef.current) return;

        if (!response.ok) {
          throw Object.assign(new Error(payload?.detail || payload?.message), {
            status: response.status,
          });
        }
        const parsed = parseHolderDetailsPayload(payload);
        if (!parsed.holder) {
          throw Object.assign(new Error("Holder not found"), { status: 404 });
        }
        setHolder(parsed.holder);
        setSummary(parsed.summary);
        setCredentials(parsed.credentials);
        setNext(parsed.next);
        setPrevious(parsed.previous);
        setOffset(nextOffset);
        setSelectedIds([]);
        hasLoadedRef.current = true;

        if (nextOffset === 0) {
          const newest = [...parsed.credentials].sort((a, b) => {
            const aTime = new Date(a.issued_at || 0).getTime();
            const bTime = new Date(b.issued_at || 0).getTime();
            return bTime - aTime;
          })[0];
          setLastIssuedAt(newest?.issued_at || null);
        }
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        if (!hasLoadedRef.current) {
          setCredentials([]);
          setNext(null);
          setPrevious(null);
          setHolder(null);
          setSummary(null);
          setLastIssuedAt(null);
          setError(classifyApiError(err));
        } else {
          Toast({
            type: "error",
            title: t("workspace:error"),
            message: t("holders:load_detail_error_title"),
          });
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setCredentialsLoading(false);
        }
      }
    },
    [holderPublicId, t],
  );

  useEffect(() => {
    hasLoadedRef.current = false;
    setOffset(0);
    setLastIssuedAt(null);
    setHolder(null);
    setSummary(null);
    setCredentials([]);
    setSelectedIds([]);
    setNext(null);
    setPrevious(null);
    loadDetails(0);
  }, [loadDetails]);

  const sortedCredentials = useMemo(() => {
    return [...credentials].sort((a, b) => {
      const aTime = new Date(a.issued_at || 0).getTime();
      const bTime = new Date(b.issued_at || 0).getTime();
      return bTime - aTime;
    });
  }, [credentials]);

  const lastIssuedDate = useMemo(
    () => formatDateOnly(lastIssuedAt, t("holders:na")),
    [lastIssuedAt, t],
  );

  const selectedCredentials = useMemo(
    () =>
      sortedCredentials.filter((c) => selectedIds.includes(c.credential_id)),
    [sortedCredentials, selectedIds],
  );

  const eligibleSelected = useMemo(
    () => selectedCredentials.filter((c) => isRevokeEligibleStatus(c.status)),
    [selectedCredentials],
  );

  const notifyEligibleSelected = useMemo(
    () => selectedCredentials.filter((c) => isNotifyEligibleStatus(c.status)),
    [selectedCredentials],
  );

  const notifyDisabled = notifyEligibleSelected.length === 0;
  const revokeDisabled = eligibleSelected.length === 0;

  const notifyTooltipMessage =
    selectedIds.length === 0
      ? t("holders:notify_tooltip")
      : t("holders:notify_ineligible_tooltip");

  const handleRowSelect = (rowId, isSelected) => {
    setSelectedIds((prev) =>
      isSelected ? [...prev, rowId] : prev.filter((id) => id !== rowId),
    );
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedIds(sortedCredentials.map((c) => c.credential_id));
      return;
    }
    setSelectedIds([]);
  };

  const resetNotifyState = () => {
    setIsNotifyOpen(false);
    setNotifyRecipients([]);
    setNotifyExclusion(null);
    setIsMultipleSelect(false);
    setToEmails([]);
    setCurrentToEmail("");
    setToEmailError("");
    setCcEmails([]);
    setCurrentCcEmail("");
    setCcEmailError("");
  };

  const handleOpenNotify = () => {
    if (notifyEligibleSelected.length === 0 || !holder) return;

    const excludedCount =
      selectedCredentials.length - notifyEligibleSelected.length;
    setNotifyExclusion(
      excludedCount > 0
        ? {
            excluded: excludedCount,
            included: notifyEligibleSelected.length,
          }
        : null,
    );

    const recipients = notifyEligibleSelected.map((credential) => ({
      id: credential.credential_id,
      email: holder.email,
      name: holder.holder_name,
    }));
    setNotifyRecipients(recipients);
    setIsMultipleSelect(recipients.length > 1);
    setToEmails(holder.email ? [holder.email] : []);
    setIsNotifyOpen(true);
  };

  const handleRequestConfirmation = useCallback((data) => {
    setIsNotifyOpen(false);
    setConfirmSendData(data);
    setTimeout(() => setShowConfirmSend(true), 250);
  }, []);

  const handleConfirmSend = useCallback(async () => {
    if (!confirmSendData?.sendPayload) return;
    try {
      setIsSendingFromConfirm(true);
      const response = await POST(
        EntityConstants.SEND_EMAIL,
        confirmSendData.sendPayload,
      );
      const data = await response.json();
      if (response.ok) {
        Toast({
          type: "success",
          title: t("workspace:success"),
          message: t("workspace:emails_sent_successfully"),
        });
        setShowConfirmSend(false);
        setConfirmSendData(null);
        resetNotifyState();
        setSelectedIds([]);
        loadDetails(offset);
      } else {
        throw new Error(data.message || t("workspace:failed_send_emails"));
      }
    } catch (err) {
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: err?.message || t("workspace:failed_send_emails"),
      });
    } finally {
      setIsSendingFromConfirm(false);
    }
  }, [confirmSendData, loadDetails, offset, t]);

  const handleCancelConfirm = useCallback(() => {
    setShowConfirmSend(false);
    setConfirmSendData(null);
    setTimeout(() => setIsNotifyOpen(true), 250);
  }, []);

  const handleToEmailChange = (e) => {
    const email = e.target.value;
    setCurrentToEmail(email);
    if (!email || emailRegex.test(email.trim())) setToEmailError("");
  };

  const handleAddToEmail = () => {
    const email = currentToEmail.trim();
    if (emailRegex.test(email)) {
      if (toEmails.includes(email)) {
        setToEmailError(`* ${t("workspace:email_already_exists")}`);
      } else {
        setToEmails([...toEmails, email]);
        setCurrentToEmail("");
        setToEmailError("");
      }
    } else if (currentToEmail) {
      setToEmailError(`* ${t("workspace:please_enter_valid_email")}`);
    }
  };

  const handleRemoveToEmail = (email) => {
    setToEmails(toEmails.filter((item) => item !== email));
    setToEmailError("");
  };

  const handlePasteToEmail = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const emailMatches =
      pastedText.match(new RegExp(emailccRegex.source, "g")) || [];
    const uniqueEmails = [
      ...new Set(emailMatches.map((email) => email.trim())),
    ];

    uniqueEmails.forEach((email) => {
      if (toEmails.includes(email)) {
        setToEmailError(
          `* ${t("workspace:email")} "${email}" ${t(
            "workspace:already_exists",
          )}`,
        );
      } else {
        setToEmails((prev) => [...prev, email]);
      }
    });

    const invalidEmails = pastedText
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(
        (email) =>
          email && !emailccRegex.test(email) && !uniqueEmails.includes(email),
      );

    if (invalidEmails.length > 0) {
      setToEmailError(
        `${t("workspace:invalid_emails_list")} ${invalidEmails.join(", ")}`,
      );
    } else {
      setToEmailError("");
    }

    setCurrentToEmail("");
  };

  const handleCcEmailChange = (e) => {
    const email = e.target.value;
    setCurrentCcEmail(email);
    if (!email || emailRegex.test(email.trim())) setCcEmailError("");
  };

  const handleAddCcEmail = () => {
    const email = currentCcEmail.trim();
    if (emailRegex.test(email)) {
      if (ccEmails.includes(email)) {
        setCcEmailError(`* ${t("workspace:email_already_exists")}`);
      } else {
        setCcEmails([...ccEmails, email]);
        setCurrentCcEmail("");
        setCcEmailError("");
      }
    } else if (currentCcEmail) {
      setCcEmailError(`* ${t("workspace:please_enter_valid_email")}`);
    }
  };

  const handleRemoveCcEmail = (email) => {
    setCcEmails(ccEmails.filter((item) => item !== email));
    setCcEmailError("");
  };

  const handlePasteCcEmail = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const emailMatches =
      pastedText.match(new RegExp(emailccRegex.source, "g")) || [];
    const uniqueEmails = [
      ...new Set(emailMatches.map((email) => email.trim())),
    ];

    uniqueEmails.forEach((email) => {
      if (ccEmails.includes(email)) {
        setCcEmailError(
          `* ${t("workspace:email")} "${email}" ${t(
            "workspace:already_exists",
          )}`,
        );
      } else {
        setCcEmails((prev) => [...prev, email]);
      }
    });

    const invalidEmails = pastedText
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(
        (email) =>
          email && !emailccRegex.test(email) && !uniqueEmails.includes(email),
      );

    if (invalidEmails.length > 0) {
      setCcEmailError(
        `${t("workspace:invalid_emails_list")} ${invalidEmails.join(", ")}`,
      );
    } else {
      setCcEmailError("");
    }

    setCurrentCcEmail("");
  };

  const handleRevokeConfirm = async () => {
    if (eligibleSelected.length === 0) return;
    setIsRevoking(true);
    let successCount = 0;
    let failCount = 0;

    for (const credential of eligibleSelected) {
      try {
        const response = await POST(EntityConstants.REVOKE_CREDENTIAL, {
          credential_id: credential.credential_id,
        });
        if (response.ok) {
          successCount += 1;
        } else {
          failCount += 1;
        }
      } catch {
        failCount += 1;
      }
    }

    setIsRevoking(false);
    setShowRevokeConfirm(false);

    if (successCount > 0 && failCount === 0) {
      Toast({
        type: "success",
        title: t("workspace:success"),
        message: t("holders:revoke_success"),
      });
    } else if (successCount > 0) {
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("holders:revoke_partial_error"),
      });
    } else {
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("holders:revoke_error"),
      });
    }

    setSelectedIds([]);
    loadDetails(offset);
  };

  const handleDownload = useCallback(
    async (credentialId) => {
      try {
        const response = await GET(
          `${EntityConstants.DOWNLOAD_SVG_AS_PNG}${credentialId}`,
        );
        if (!response.ok) {
          throw new Error("Download failed");
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${credentialId}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("history:failed_download_certificate"),
        });
      }
    },
    [t],
  );

  const columns = useMemo(
    () => [
      {
        key: "credential_name",
        label: t("holders:column_credential"),
        render: (_value, row) => (
          <div className="inline-flex items-center justify-center gap-3 min-w-[14rem] text-left">
            <p className="font-medium text-cfHeadingText">
              {row.credential_name || t("holders:na")}
            </p>
          </div>
        ),
      },
      {
        key: "credential_id",
        label: t("holders:column_credential_id"),
      },
      {
        key: "issuer_name",
        label: t("holders:column_issuer"),
        render: (value) => value || t("holders:na"),
      },
      {
        key: "issued_at",
        label: t("holders:column_issued_on"),
        render: (value) => formatDateOnly(value, t("holders:na")),
      },
      {
        key: "status",
        label: t("holders:column_status"),
        render: (value) => {
          const statusClass = getIssuanceStatusBadgeClass(value);
          const showCheck = isRevokeEligibleStatus(value);
          return (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClass}`}
            >
              {showCheck ? <Check className="h-3 w-3" aria-hidden /> : null}
              {getIssuanceStatusLabel(value, t)}
            </span>
          );
        },
      },
      {
        key: "actions",
        label: t("holders:column_actions"),
        sortable: false,
        render: (_value, row) => (
          <div className="inline-flex items-center justify-center gap-1">
            <button
              type="button"
              title={t("holders:view")}
              aria-label={t("holders:view")}
              onClick={(event) => {
                event.stopPropagation();
                setPreviewCredential(row);
              }}
              className="inline-flex items-center justify-center rounded-md border-0 bg-transparent p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-cfPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-1"
            >
              <EyeIcon size={16} aria-hidden />
            </button>
            <button
              type="button"
              title={t("holders:download")}
              aria-label={t("holders:download")}
              onClick={(event) => {
                event.stopPropagation();
                handleDownload(row.credential_id);
              }}
              className="inline-flex items-center justify-center rounded-md border-0 bg-transparent p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-cfPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-1"
            >
              <DownloadIcon size={16} aria-hidden />
            </button>
          </div>
        ),
      },
    ],
    [handleDownload, t],
  );

  if (loading) {
    return (
      <div
        className="min-h-screen bg-cfSecondary pt-20 pb-10"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4">
          <div className="flex items-center min-h-[4rem] gap-2 text-sm text-gray-500 w-full min-w-0">
            <button
              type="button"
              onClick={() => navigate("/issuer/holders")}
              className="hover:text-gray-800 shrink-0"
            >
              {t("holders:breadcrumb_holders")}
            </button>
            <ChevronLeft className="h-4 w-4 text-gray-500 transform rotate-180 shrink-0" />
            <HolderDetailsShimmer className="h-4 w-40 max-w-[40%]" />
          </div>
        </div>

        <div className="px-4 md:px-8 mt-4">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 py-2">
            <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
              <HolderDetailsShimmer className="h-16 w-16 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-3 pt-1">
                <div className="flex flex-wrap items-center gap-2">
                  <HolderDetailsShimmer className="h-7 w-48 max-w-full" />
                  <HolderDetailsShimmer className="h-6 w-20 rounded-full" />
                </div>
                <HolderDetailsShimmer className="h-4 w-56 max-w-full" />
                <HolderDetailsShimmer className="h-4 w-36" />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <HolderDetailsShimmer className="h-10 w-28 rounded-md" />
              <HolderDetailsShimmer className="h-10 w-28 rounded-md" />
              <HolderDetailsShimmer className="h-10 w-40 rounded-md" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-4 mb-6">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`holder-detail-kpi-skeleton-${index}`}
                className="flex items-center gap-3.5 py-[1.4rem] px-5 bg-white border-[1.5px] border-gray-200 rounded-xl"
              >
                <HolderDetailsShimmer className="h-11 w-11 rounded-[10px] shrink-0" />
                <div className="flex flex-col gap-2 min-w-0 flex-1">
                  <HolderDetailsShimmer className="h-3.5 w-24 max-w-full" />
                  <HolderDetailsShimmer className="h-7 w-14" />
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <div className="space-y-2">
              <HolderDetailsShimmer className="h-5 w-44" />
              <HolderDetailsShimmer className="h-4 w-56 max-w-full" />
            </div>
            <HolderDetailsShimmer className="h-4 w-36" />
          </div>

          <DataTable
            columns={columns}
            data={[]}
            loading
            loadingVariant="skeleton"
            skeletonRowCount={PAGE_LIMIT}
            selectable
            selectedRows={[]}
            rowKey="credential_id"
            align="center"
            dataTestId="holder-credentials-table"
            showPagination={false}
            emptyMessage=""
          />
        </div>
      </div>
    );
  }

  if (error || !holder) {
    const errorCopy = error ? getApiErrorCopy(error, t, "finance") : null;
    return (
      <div className="min-h-screen bg-cfSecondary pt-20 pb-10">
        <div className="w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4">
          <div className="flex items-center min-h-[4rem] gap-2 text-sm text-gray-500 w-full">
            <button
              type="button"
              onClick={() => navigate("/issuer/holders")}
              className="hover:text-gray-800"
            >
              {t("holders:breadcrumb_holders")}
            </button>
            <ChevronLeft className="h-4 w-4 text-gray-500 transform rotate-180" />
            <span className="text-gray-800 font-medium">
              {error?.kind === "not_found"
                ? t("holders:not_found_title")
                : t("holders:load_detail_error_title")}
            </span>
          </div>
        </div>
        <div className="px-4 md:px-8 mt-8">
          <PageErrorState
            kind={error?.kind}
            title={
              error?.kind === "not_found"
                ? t("holders:not_found_title")
                : t("holders:load_detail_error_title")
            }
            description={
              error?.kind === "not_found"
                ? t("holders:not_found_description")
                : errorCopy?.description
            }
            primaryLabel={t("holders:retry")}
            onPrimary={() => loadDetails(0)}
            secondaryLabel={t("holders:breadcrumb_holders")}
            onSecondary={() => navigate("/issuer/holders")}
          />
        </div>
      </div>
    );
  }

  const initials = getHolderInitials(holder.holder_name);
  const colors = getAvatarColors(
    holder.holder_public_id || holder.email || holder.holder_name,
  );
  const overallTone =
    STATUS_TONE_STYLES[getCredentialStatusTone(holder.overall_status)] ||
    STATUS_TONE_STYLES.neutral;

  const summaryCards = [
    {
      key: "total",
      label: t("holders:summary_credentials"),
      value: summary?.total_credentials ?? 0,
      iconBg: "bg-[#f4f0fc]",
      iconSrc: idCardIcon,
    },
    {
      key: "active",
      label: t("holders:summary_active"),
      value: summary?.active ?? 0,
      iconBg: "bg-[#ecfdf3]",
      iconSrc: correctGreenIcon,
    },
    {
      key: "expired",
      label: t("holders:summary_expired"),
      value: summary?.expired ?? 0,
      iconBg: "bg-[#F6F6F6]",
      iconSrc: crossIcon,
    },
    {
      key: "revoked",
      label: t("holders:summary_revoked"),
      value: summary?.revoked ?? 0,
      iconBg: "bg-[#fceeef]",
      iconSrc: dangerIcon,
    },
    // TODO: Re-enable when verification feature is implemented
    // {
    //   key: "verifications",
    //   label: t("holders:summary_verifications"),
    //   value: summary?.verifications ?? 0,
    //   iconBg: "bg-[#eff4ff]",
    //   iconSrc: arrowSquareUpRightIcon,
    // },
  ];

  return (
    <div className="min-h-screen bg-cfSecondary pt-20 pb-10">
      <div className="w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4">
        <div className="flex items-center min-h-[4rem] gap-2 text-sm text-gray-500 w-full min-w-0">
          <button
            type="button"
            onClick={() => navigate("/issuer/holders")}
            className="hover:text-gray-800 shrink-0"
          >
            {t("holders:breadcrumb_holders")}
          </button>
          <ChevronLeft className="h-4 w-4 text-gray-500 transform rotate-180 shrink-0" />
          <span className="text-gray-800 font-medium truncate">
            {holder.holder_name}
          </span>
        </div>
      </div>

      <div className="px-4 md:px-8 mt-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 py-2">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div
              className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-xl font-bold"
              style={{ backgroundColor: colors.bg, color: colors.text }}
              aria-hidden
            >
              {initials}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900">
                  {holder.holder_name}
                </h1>
                {holder.overall_status ? (
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${overallTone.badgeClass}`}
                  >
                    {getCredentialStatusTone(holder.overall_status) ===
                    "success" ? (
                      <Check className="h-3 w-3" aria-hidden />
                    ) : null}
                    {getIssuanceStatusLabel(holder.overall_status, t)}
                  </span>
                ) : null}
              </div>
              {holder.email ? (
                <a
                  href={`mailto:${holder.email}`}
                  className="mt-1 block truncate text-sm text-cfPrimary hover:underline"
                >
                  {holder.email}
                </a>
              ) : null}
              <p className="mt-2 text-sm text-gray-500">
                {t("holders:enrolled_on")}:{" "}
                <span className="text-cfHeadingText font-semibold">
                  {formatDateOnly(holder.enrollment_date, t("holders:na"))}
                </span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative group">
              <button
                type="button"
                disabled={notifyDisabled}
                onClick={handleOpenNotify}
                className={`flex items-center gap-2 px-4 py-[0.66rem] rounded-md text-sm font-medium text-white transition-all duration-300 ease-in-out shadow-sm ${
                  notifyDisabled
                    ? "bg-indigo-300 cursor-not-allowed"
                    : "bg-cfButton hover:bg-cfPrimaryDark hover:shadow-md"
                }`}
              >
                <Mail className="h-4 w-4" aria-hidden />
                {t("holders:notify")}
              </button>
              {notifyDisabled ? (
                <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-56 -translate-x-1/2 rounded-md bg-cfHeadingText px-3 py-2 text-xs text-white shadow-lg group-hover:block">
                  {notifyTooltipMessage}
                </span>
              ) : null}
            </div>

            <div className="relative group">
              <button
                type="button"
                disabled={revokeDisabled}
                onClick={() => setShowRevokeConfirm(true)}
                className={`flex items-center gap-2 px-4 py-[0.66rem] rounded-md text-sm font-medium border transition-all duration-300 ${
                  revokeDisabled
                    ? "border-gray-300 text-gray-400 cursor-not-allowed bg-white"
                    : "border-cfStatusBlockedText text-cfStatusBlockedText bg-white hover:bg-cfStatusBGBlocked"
                }`}
              >
                <ShieldOff className="h-4 w-4" aria-hidden />
                {t("holders:revoke")}
              </button>
              {revokeDisabled ? (
                <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-56 -translate-x-1/2 rounded-md bg-cfHeadingText px-3 py-2 text-xs text-white shadow-lg group-hover:block">
                  {t("holders:revoke_tooltip")}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-4 mb-6">
          {summaryCards.map(({ key, label, value, iconBg, iconSrc }) => (
            <div
              key={key}
              className="flex items-center gap-3.5 py-[1.4rem] px-4 bg-white border-[1.5px] border-gray-200 rounded-xl transition-all duration-200 hover:shadow-md"
            >
              <div
                className={`${iconBg} flex items-center justify-center w-11 h-11 rounded-[10px] flex-shrink-0`}
              >
                <img src={iconSrc} alt="" className="w-6 h-6" aria-hidden />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-gray-500 mb-0.5">
                  {label}
                </span>
                <span className="text-2xl font-bold text-gray-900 leading-tight">
                  {value}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <div>
            <h2 className="text-base font-semibold text-cfHeadingText">
              {t("holders:credentials_section_title")}
            </h2>
            <p className="text-sm text-gray-500">
              {t("holders:credentials_section_meta")} {lastIssuedDate}
            </p>
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto">
            <p className="text-sm text-gray-500">
              {t("holders:selected_count", { count: selectedIds.length })}
              {selectedIds.length === 0
                ? ` · ${t("holders:select_hint")}`
                : null}
            </p>
            <div className="flex items-center border border-gray-300 rounded-md bg-white overflow-hidden shrink-0">
              <button
                type="button"
                id="holder-credentials-refresh-table"
                onClick={() => loadDetails(offset)}
                disabled={credentialsLoading || loading}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                title={t("holders:refresh_table")}
                aria-label={t("holders:refresh_table")}
              >
                <RefreshCWIcon
                  size={16}
                  className={`inline-flex ${
                    credentialsLoading ? "animate-spin" : ""
                  }`}
                  aria-hidden
                />
              </button>
            </div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={sortedCredentials}
          loading={credentialsLoading}
          loadingVariant="skeleton"
          skeletonRowCount={PAGE_LIMIT}
          emptyMessage={t("holders:empty_credentials")}
          emptyHint={t("holders:empty_credentials_description")}
          selectable
          selectedRows={selectedIds}
          onRowSelect={handleRowSelect}
          onSelectAll={handleSelectAll}
          rowKey="credential_id"
          align="center"
          dataTestId="holder-credentials-table"
          hasPrevious={Boolean(previous)}
          hasNext={Boolean(next)}
          onPrevious={() => loadDetails(Math.max(0, offset - PAGE_LIMIT))}
          onNext={() => loadDetails(offset + PAGE_LIMIT)}
          showPagination
        />
      </div>

      <CertificatePreviewModal
        isOpen={Boolean(previewCredential)}
        onClose={() => setPreviewCredential(null)}
        previewUrl={previewCredential?.credential_digital_preview}
        printPreviewUrl={previewCredential?.credential_print_preview}
        credential_id={previewCredential?.credential_id}
        isLoading={false}
      />

      {isNotifyOpen ? (
        <EmailPreviewModal
          recipient={isMultipleSelect ? null : notifyRecipients[0]}
          recipients={isMultipleSelect ? notifyRecipients : []}
          isOpen={isNotifyOpen}
          onClose={resetNotifyState}
          isMultipleSelect={isMultipleSelect}
          toEmails={toEmails}
          currentToEmail={currentToEmail}
          toEmailError={toEmailError}
          onToEmailChange={handleToEmailChange}
          onAddToEmail={handleAddToEmail}
          onRemoveToEmail={handleRemoveToEmail}
          onPasteToEmail={handlePasteToEmail}
          ccEmails={ccEmails}
          setCcEmails={setCcEmails}
          currentCcEmail={currentCcEmail}
          ccEmailError={ccEmailError}
          onCcEmailChange={handleCcEmailChange}
          onAddCcEmail={handleAddCcEmail}
          onRemoveCcEmail={handleRemoveCcEmail}
          onPasteCcEmail={handlePasteCcEmail}
          onRequestConfirmation={handleRequestConfirmation}
          exclusionNotice={
            notifyExclusion
              ? {
                  title: t("holders:notify_excluded_title"),
                  message: t("holders:notify_excluded_message", {
                    excluded: notifyExclusion.excluded,
                    included: notifyExclusion.included,
                  }),
                }
              : null
          }
        />
      ) : null}

      <ConfirmSendModal
        isOpen={showConfirmSend}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirmSend}
        recipientCount={confirmSendData?.recipientCount || 0}
        totalTo={confirmSendData?.totalTo || 0}
        totalCc={confirmSendData?.totalCc || 0}
        isSending={isSendingFromConfirm}
        isNotifyAll={false}
      />

      <Transition appear show={showRevokeConfirm} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40"
          onClose={() => !isRevoking && setShowRevokeConfirm(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-cfHeadingText">
                    {t("holders:revoke_confirm_title")}
                  </Dialog.Title>
                  <p className="mt-3 text-sm text-gray-500">
                    {t("holders:revoke_confirm_message", {
                      count: eligibleSelected.length,
                    })}
                  </p>
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      disabled={isRevoking}
                      onClick={() => setShowRevokeConfirm(false)}
                      className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-cfHeadingText hover:bg-gray-50"
                    >
                      {t("holders:revoke_cancel")}
                    </button>
                    <button
                      type="button"
                      disabled={isRevoking}
                      onClick={handleRevokeConfirm}
                      className="rounded-md bg-cfStatusBlockedText px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                    >
                      {t("holders:revoke_confirm_action")}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default HolderDetailsPage;
